import { toJpeg } from 'html-to-image';

export const MAX_PREVIEW_DATA_URL_LENGTH = 2_500_000;
const IMAGE_DECODE_TIMEOUT_MS = 15000;

type RendererOptions = NonNullable<Parameters<typeof toJpeg>[1]>;
type Renderer = (element: HTMLElement, options: RendererOptions) => Promise<string>;

export async function createCarouselPreview(
  element: HTMLElement,
  render: Renderer = toJpeg
): Promise<string> {
  const images = Array.from(element.querySelectorAll('img'));
  await Promise.all(images.map(image =>
    image.complete && image.naturalWidth > 0
      ? Promise.resolve()
      // A broken/removed image (e.g. a category content's optional image whose
      // file is gone) rejects decode(); that must not abort the whole preview.
      // Raced against a timeout so a stalled download can never hang the capture.
      : Promise.race([
          image.decode().catch(() => undefined),
          new Promise<void>(resolve => setTimeout(resolve, IMAGE_DECODE_TIMEOUT_MS))
        ])
  ));
  await document.fonts?.ready;
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

  const scale = Math.min(1, 1200 / Math.max(1, element.clientWidth));
  const dataUrl = await render(element, {
    quality: 0.82,
    pixelRatio: 1,
    canvasWidth: Math.round(element.clientWidth * scale),
    canvasHeight: Math.round(element.clientHeight * scale),
    backgroundColor: getComputedStyle(element).backgroundColor || '#ffffff',
    // Images are served with Access-Control-Allow-Origin: * so the browser-cached copies are
    // reusable; busting the cache would re-download everything over a slow connection.
    cacheBust: false,
    skipFonts: true,
    filter: (node: Node) => !(node instanceof HTMLElement && node.classList?.contains('no-capture'))
  });

  if (dataUrl.length > MAX_PREVIEW_DATA_URL_LENGTH) {
    throw new Error('Carousel preview is too large.');
  }

  return dataUrl;
}
